/**
 * Client-side validation and the mapping of API `errors` keys back onto our fields and panes.
 * Field keys are API-shaped so a 422 needs no translation table for system fields.
 */
import type { FieldErrors } from '../api/errors';
import { normalizeControlType } from '../api/normalize/controlType';
import type { BookingFormConfig, CustomField, Service } from '../api/types';
import { isValueTruthy, sortByOrdering, type CustomFieldValues } from './customFieldValues';
import { outsideServiceStatesMessage, zipMatchesStates } from './geo';
import { DEFAULT_SYSTEM_LABELS, stripRequiredMarker } from './labels';
import type { FormValues } from './payload';
import { htmlToText } from './sanitize';

export type Pane = 1 | 2 | 3;

export const STEP1_ORDER = [
  'user.first_name',
  'user.last_name',
  'user.email',
  'phone',
  'address',
  'apt_suite',
  'city',
  'state',
  'zip',
  'service_date',
] as const;

export const STEP2_ORDER = ['services', 'frequency_id', 'custom_fields'] as const;

export const STEP3_ORDER = ['discount_code', 'payment_method', 'card', 'recaptcha_token', 'tip', 'customer_notes'] as const;

/**
 * Which pane a field lives on. Custom fields sit on pane 2 unless the tenant placed them before
 * the submit button (`settings.before_submit_button`), which our layout renders on pane 3.
 */
export function paneForKey(key: string, beforeSubmitCustomFieldIds: readonly number[] = []): Pane | null {
  if (key.startsWith('custom_fields')) {
    const id = Number(key.split('.')[1]);
    return Number.isFinite(id) && beforeSubmitCustomFieldIds.includes(id) ? 3 : 2;
  }
  if ((STEP1_ORDER as readonly string[]).includes(key)) return 1;
  if ((STEP2_ORDER as readonly string[]).includes(key)) return 2;
  if ((STEP3_ORDER as readonly string[]).includes(key)) return 3;
  return null;
}

export const customFieldKey = (id: number): string => `custom_fields.${id}`;

/** Fields the API itself requires; everything else follows the tenant's config. */
const API_REQUIRED_SYSTEM = new Set(['first_name', 'last_name', 'email', 'address']);

type SystemFieldCode = keyof NonNullable<BookingFormConfig['system_fields']>;

export interface SystemFieldRequirement {
  code: string;
  label: string;
  shown: boolean;
  required: boolean;
}

export interface RequirementOptions {
  /** ZIP is needed to check the service area when out-of-area bookings are blocked */
  requireZip: boolean;
}

export function systemFieldRequirement(
  form: BookingFormConfig | null,
  code: string,
  opts: RequirementOptions,
): SystemFieldRequirement {
  const field = form?.system_fields?.[code as SystemFieldCode];
  const { text, markedRequired } = stripRequiredMarker(field?.label);
  return {
    code,
    label: text || DEFAULT_SYSTEM_LABELS[code] || code,
    shown: field ? field.show !== false : true,
    required:
      API_REQUIRED_SYSTEM.has(code) ||
      field?.value_required === true ||
      markedRequired ||
      (code === 'zip' && opts.requireZip),
  };
}

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const US_ZIP_RE = /^\d{5}(-\d{4})?$/;

export interface Step1Options extends RequirementOptions {
  countryCode: string | null;
  /** message from a failed service-area check, attached to the ZIP field */
  locationBlockedMessage: string | null;
  /** state codes the business serves; empty means no restriction */
  serviceStates?: readonly string[];
  /** the tenant's names for those codes, for the customer-facing message */
  serviceStateNames?: readonly string[];
}

const STEP1_FIELDS: [code: string, key: string, read: (v: FormValues) => string][] = [
  ['first_name', 'user.first_name', (v) => v.user.first_name],
  ['last_name', 'user.last_name', (v) => v.user.last_name],
  ['email', 'user.email', (v) => v.user.email],
  ['phone', 'phone', (v) => v.phone],
  ['address', 'address', (v) => v.address],
  ['apt_suite', 'apt_suite', (v) => v.apt_suite],
  ['city', 'city', (v) => v.city],
  ['state', 'state', (v) => v.state],
  ['zip', 'zip', (v) => v.zip],
];

export function validateStep1(values: FormValues, form: BookingFormConfig | null, opts: Step1Options): FieldErrors {
  const errors: FieldErrors = {};
  for (const [code, key, read] of STEP1_FIELDS) {
    const req = systemFieldRequirement(form, code, opts);
    const value = read(values).trim();
    if (req.shown && req.required && !value) errors[key] = [`${req.label} is required`];
  }
  const email = values.user.email.trim();
  if (email && !EMAIL_RE.test(email)) errors['user.email'] = ['Enter a valid email address'];
  const digits = values.phone.replace(/\D/g, '');
  if (values.phone.trim() && digits.length < 7) errors['phone'] = ['Enter a valid phone number'];
  const zip = values.zip.trim();
  if (zip && (opts.countryCode ?? 'US') === 'US' && !US_ZIP_RE.test(zip)) errors['zip'] = ['Enter a valid ZIP code'];
  else if (zip && zipMatchesStates(zip, opts.serviceStates ?? []) === 'mismatch') {
    errors['zip'] = [outsideServiceStatesMessage(opts.serviceStateNames ?? opts.serviceStates ?? [])];
  }
  if (opts.locationBlockedMessage) errors['zip'] = [opts.locationBlockedMessage];
  if (!values.slot) errors['service_date'] = ['Choose a date and time'];
  return errors;
}

/** Required, visible custom fields of one placement that are still unanswered. */
export function requiredCustomFieldErrors(fields: CustomField[], values: FormValues, visibleCodes: Set<string>): FieldErrors {
  const errors: FieldErrors = {};
  for (const field of sortByOrdering(fields)) {
    if (typeof field.id !== 'number' || !field.code || !visibleCodes.has(field.code)) continue;
    const { type } = normalizeControlType(field.control_type);
    if (type === 'static' || field.value_required !== true) continue;
    if (!isValueTruthy(values.customFields[field.id])) {
      const text = htmlToText(field.label ?? '') || 'This field';
      errors[customFieldKey(field.id)] = [type === 'checkbox' ? 'Please tick this box to continue' : `${stripRequiredMarker(text).text} is required`];
    }
  }
  return errors;
}

export interface Step2Input {
  values: FormValues;
  form: BookingFormConfig | null;
  services: Service[];
  /** custom fields NOT placed before the submit button */
  fields: CustomField[];
  visibleCodes: Set<string>;
}

export function validateStep2(input: Step2Input): FieldErrors {
  const { values, services, fields, visibleCodes } = input;
  const errors: FieldErrors = {};
  const label = (code: string, fallback: string) => systemFieldRequirement(input.form, code, { requireZip: false }).label || fallback;

  const selection = values.services[0];
  if (!selection) errors['services'] = [`Choose a ${label('service', 'service').toLowerCase()}`];
  else {
    const service = services.find((s) => s.id === selection.serviceId);
    if (!service) errors['services'] = ['That service is no longer available'];
    else {
      for (const param of service.pricing_parameters ?? []) {
        if (typeof param.id !== 'number') continue;
        const qty = selection.pricingParameters[param.id] ?? 0;
        const min = param.quantity_minimum ?? 0;
        const max = param.quantity_maximum ?? Number.POSITIVE_INFINITY;
        if (qty < min || qty > max) {
          errors['services'] = [`${param.name ?? 'Quantity'} must be between ${min} and ${Number.isFinite(max) ? max : '∞'}`];
        }
      }
    }
  }
  if (values.frequencyId === null) errors['frequency_id'] = [`Choose ${label('frequency', 'how often').toLowerCase()}`];
  Object.assign(errors, requiredCustomFieldErrors(fields, values, visibleCodes));
  return errors;
}

export interface Step3Input {
  values: FormValues;
  /** custom fields placed before the submit button (e.g. the terms checkbox) */
  fields: CustomField[];
  visibleCodes: Set<string>;
  cardComplete: boolean;
  recaptchaRequired: boolean;
  recaptchaToken: string | null;
}

export function validateStep3(input: Step3Input): FieldErrors {
  const { values } = input;
  const errors: FieldErrors = {};
  if (!values.paymentMethod) errors['payment_method'] = ['Choose a payment method'];
  else if (values.paymentMethod === 'stripe' && !input.cardComplete) errors['card'] = ['Enter your card details'];
  if (input.recaptchaRequired && !input.recaptchaToken) errors['recaptcha_token'] = ['Please complete the captcha'];
  Object.assign(errors, requiredCustomFieldErrors(input.fields, values, input.visibleCodes));
  return errors;
}

export interface CustomFieldPlacement {
  mainIds?: number[];
  beforeSubmitIds?: number[];
}

/** First erroring key in each pane's display order. */
export function firstErrorKey(errors: FieldErrors, pane: Pane, placement: CustomFieldPlacement = {}): string | null {
  const order: string[] =
    pane === 1
      ? [...STEP1_ORDER]
      : pane === 2
        ? ['services', 'frequency_id', ...(placement.mainIds ?? []).map(customFieldKey), 'custom_fields']
        : ['discount_code', 'payment_method', 'card', 'recaptcha_token', ...(placement.beforeSubmitIds ?? []).map(customFieldKey), 'tip', 'customer_notes'];
  for (const key of order) if (errors[key]?.length) return key;
  const any = Object.keys(errors).find((k) => paneForKey(k, placement.beforeSubmitIds ?? []) === pane);
  return any ?? null;
}

// ---------- API error mapping ----------

const API_KEY_ALIASES: [RegExp, string][] = [
  [/^(user\.)?email$/, 'user.email'],
  [/^(user\.)?first_name$/, 'user.first_name'],
  [/^(user\.)?last_name$/, 'user.last_name'],
  [/^user$/, 'user.email'],
  [/^(phone|phone_number|user\.phone)$/, 'phone'],
  [/^(address|address_wo_suite|street|location_id|location)$/, 'address'],
  [/^(apt_suite|apt|suite)$/, 'apt_suite'],
  [/^city$/, 'city'],
  [/^state$/, 'state'],
  [/^(zip|zip_code|zipcode|postal_code)$/, 'zip'],
  [/^services(\..*)?$/, 'services'],
  [/^(service_date|spot|date|service_time|arrival_window)$/, 'service_date'],
  [/^(frequency_id|frequency)$/, 'frequency_id'],
  [/^(discount_code|discount|gift_?card(_code)?|code)$/, 'discount_code'],
  [/^payment_method$/, 'payment_method'],
  [/^(stripe.*|card.*|fspay.*|payment)$/, 'card'],
  [/^(recaptcha.*|captcha.*|g-recaptcha-response)$/, 'recaptcha_token'],
  [/^tip(_recurring)?$/, 'tip'],
  [/^customer_notes$/, 'customer_notes'],
];

export interface ErrorMappingContext {
  /** ids in the order they were submitted, for `custom_fields.<index>` keys */
  submittedCustomFieldIds: number[];
  knownCustomFieldIds: number[];
  /** ids of custom fields rendered on the payment pane */
  beforeSubmitCustomFieldIds?: number[];
}

export function normalizeApiErrorKey(raw: string, ctx: ErrorMappingContext): string | null {
  const key = raw
    .trim()
    .replace(/\[(\w+)\]/g, '.$1')
    .replace(/^\./, '')
    .toLowerCase();
  const custom = /^(custom_fields|custom_values|customvalues)(?:\.(\d+))?(?:\..*)?$/.exec(key);
  if (custom) {
    if (custom[2] === undefined) return 'custom_fields';
    const n = Number(custom[2]);
    if (n < ctx.submittedCustomFieldIds.length) return customFieldKey(ctx.submittedCustomFieldIds[n]!);
    if (ctx.knownCustomFieldIds.includes(n)) return customFieldKey(n);
    return 'custom_fields';
  }
  for (const [re, target] of API_KEY_ALIASES) if (re.test(key)) return target;
  return null;
}

export interface MappedError {
  apiKey: string;
  key: string | null;
  pane: Pane | null;
  messages: string[];
}

export interface MappedErrors {
  mapped: MappedError[];
  fieldErrors: FieldErrors;
  /** messages we could not attach to a field */
  banner: string[];
  firstPane: Pane | null;
}

function humanize(key: string): string {
  return key.replace(/[._[\]]+/g, ' ').replace(/\s+/g, ' ').trim();
}

export function mapApiErrors(fieldErrors: FieldErrors, ctx: ErrorMappingContext): MappedErrors {
  const out: FieldErrors = {};
  const banner: string[] = [];
  const mapped: MappedError[] = [];
  for (const [apiKey, messages] of Object.entries(fieldErrors)) {
    const key = normalizeApiErrorKey(apiKey, ctx);
    const pane = key ? paneForKey(key, ctx.beforeSubmitCustomFieldIds ?? []) : null;
    mapped.push({ apiKey, key, pane, messages });
    if (key && pane) out[key] = [...(out[key] ?? []), ...messages];
    else banner.push(...messages.map((m) => (apiKey ? `${humanize(apiKey)}: ${m}` : m)));
  }
  const panes = mapped.map((m) => m.pane).filter((p): p is Pane => p !== null);
  return { mapped, fieldErrors: out, banner, firstPane: panes.length ? (Math.min(...panes) as Pane) : null };
}
